import React, { useState, useEffect } from "react";
import {
  getLocationPlans,
  toCurrency,
  buildIndexedArray,
  Addon,
  Location,
  BusinessPlanOptions,
  isTierOneAddon,
  isTierTwoAddon,
  isTierThreeAddon,
} from "@ecologi/shared";
import StarterIcon from "@ecologi/assets/icons/Plan/business_starter.svg";
import EssentialIcon from "@ecologi/assets/icons/Plan/business_essential.svg";
import UltimateIcon from "@ecologi/assets/icons/Plan/business_ultimate.svg";
import TreesMiniIcon from "@ecologi/assets/icons/Plan/trees_mini.svg";
import CarbonMiniIcon from "@ecologi/assets/icons/Plan/carbon_mini.svg";
import { Select } from "../../Select";
import { TextField, TextFieldChangeEvent } from "../../TextField";

import styles from "../styles/BusinessPlanTier.module.scss";

// Create <select> options for 0 -> 100 including an extra option which
// will act as a trigger to switch to the input based variant
const numEmployeeOptions = 100;
const overValue = "over";

const numberOptions = [
  ...buildIndexedArray(numEmployeeOptions + 1).map((n: number) => ({
    value: n.toString(),
    label: n.toString(),
  })),
  { value: overValue, label: `More than ${numEmployeeOptions}...` },
];

export const BusinessPlanTier = ({
  tier,
  tierKey,
  location,
  isUpdateSubscription,
  selectedBusinessPlanAddons,
  setSelectedBusinessPlanAddons,
}: {
  tier: Addon;
  tierKey: string;
  location: Location | null;
  isUpdateSubscription: boolean;
  selectedBusinessPlanAddons: BusinessPlanOptions;
  setSelectedBusinessPlanAddons: (newAddons: any) => void;
}) => {
  // Switch from dropdowns to number inputs when employees are over 100
  const [isNumberInputVariant, setIsNumberInputVariant] = useState(false);
  const { currencySymbol } = getLocationPlans(location);

  // When in update-mode, make sure we show the text inputs
  useEffect(() => {
    if (
      isUpdateSubscription &&
      selectedBusinessPlanAddons[tierKey] > numEmployeeOptions
    ) {
      setIsNumberInputVariant(true);
    }
  }, [isUpdateSubscription, selectedBusinessPlanAddons]);

  const monthlyTrees = tier.stats.trees;
  const monthlyCarbon = tier.stats.carbon;

  return (
    <div className={styles.tier}>
      <div className={styles.tierInner}>
        <div className={styles.tierHeader}>
          <h3 className={styles.tierName}>{tier.title}</h3>
          {isTierOneAddon(tier.id) && (
            <StarterIcon className={styles.tierIcon} />
          )}
          {isTierTwoAddon(tier.id) && (
            <EssentialIcon className={styles.tierIcon} />
          )}
          {isTierThreeAddon(tier.id) && (
            <UltimateIcon className={styles.tierIcon} />
          )}
        </div>
        <p className={styles.tierPrice}>
          {currencySymbol === "$" ? "US$" : currencySymbol}
          {toCurrency(tier.price)}/month
          <span className={styles.tierPriceSub}>per employee</span>
        </p>
        <p className={styles.tierDescription}>{tier.description}</p>
        <div className={styles.tierQuantityWrapper}>
          {isNumberInputVariant ? (
            <TextField
              id={`${tierKey}NumberInput`}
              label={tierKey}
              min={0}
              name={`${tierKey}NumberInput`}
              data-testid={`${tierKey}NumberInput`}
              placeholder="0"
              type="number"
              value={selectedBusinessPlanAddons[tierKey].toString()}
              onChange={(e: TextFieldChangeEvent) => {
                setSelectedBusinessPlanAddons({
                  ...selectedBusinessPlanAddons,
                  [tierKey]: Number(e.target.value) || 0,
                });
              }}
              isLabelHidden
            />
          ) : (
            <Select
              data-testid={`${tierKey}Select`}
              name={`${tierKey}Select`}
              label={`${tierKey}Select`}
              hiddenLabel
              items={numberOptions}
              onChange={(e) => {
                const val = e.target.value;
                if (val === overValue) {
                  setIsNumberInputVariant(true);
                  return;
                }
                setSelectedBusinessPlanAddons({
                  ...selectedBusinessPlanAddons,
                  [tierKey]: Number(val),
                });
              }}
              value={selectedBusinessPlanAddons[tierKey].toString()}
            />
          )}
        </div>
        <div>
          <div className={styles.tierImpact}>
            <TreesMiniIcon className={styles.tierImpactIcon} />
            <p className={styles.tierImpactText}>
              <strong>{monthlyTrees} trees</strong> funded/month
            </p>
          </div>
          <div className={styles.tierImpact}>
            <CarbonMiniIcon className={styles.tierImpactIcon} />
            <p className={styles.tierImpactText}>
              <strong>{monthlyCarbon} tonnes</strong> CO<sub>2</sub>e
              avoided/month*
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
